// useHistory.ts
// Manages evaluation persistence in Supabase.
// Saves results immediately after evaluation completes.
// Fetches history for the dashboard and history view.

import { useState, useEffect, useCallback } from 'react';
import { useUser, useAuth } from '@clerk/clerk-react';
import { makeSupabaseClient } from '../lib/supabase';
import type { EvaluationResult, BatchResult } from '../types';

export interface HistoryEntry {
  id: string;
  filename: string;
  overall_score: number;
  verdict: string;
  detected_paper_type: string;
  priority: string;
  result_json: EvaluationResult;
  created_at: string;
}

interface UseHistoryResult {
  history: HistoryEntry[];
  loading: boolean;
  totalCount: number;
  totalTimeSavedMinutes: number;
  saveEvaluation: (result: EvaluationResult) => Promise<void>;
  saveBatch: (batch: BatchResult) => Promise<void>;
  deleteEvaluation: (id: string) => Promise<void>;
  refetch: () => void;
}

export function useHistory(): UseHistoryResult {
  const { user } = useUser();
  const { getToken } = useAuth();
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    async function fetchHistory() {
      setLoading(true);
      const token = await getToken({ template: 'Supabase' });
      const supabase = makeSupabaseClient(token ?? null);

      const { data, error } = await supabase
        .from('evaluations')
        .select('*')
        .eq('user_id', user!.id)
        .order('created_at', { ascending: false })
        .limit(200);

      if (error) {
        console.error('Failed to fetch history:', error);
      } else {
        setHistory((data as HistoryEntry[]) || []);
      }
      setLoading(false);
    }

    fetchHistory();
  }, [user, getToken, refreshKey]);

  const saveEvaluation = useCallback(async (result: EvaluationResult) => {
    if (!user) return;
    const token = await getToken({ template: 'Supabase' });
    const supabase = makeSupabaseClient(token ?? null);

    const { error } = await supabase.from('evaluations').insert({
      user_id: user.id,
      filename: result.filename,
      overall_score: result.overall,
      verdict: result.verdict,
      detected_paper_type: result.detected_paper_type,
      priority: result.priority,
      result_json: result,
    });

    if (error) {
      console.error('Failed to save evaluation:', error);
    }
  }, [user, getToken]);

  const saveBatch = useCallback(async (batch: BatchResult) => {
    if (!user) return;
    const token = await getToken({ template: 'Supabase' });
    const supabase = makeSupabaseClient(token ?? null);

    const validResults = batch.results.filter(r => !r.error);
    if (validResults.length === 0) return;

    const rows = validResults.map(r => ({
      user_id: user.id,
      filename: r.filename,
      overall_score: r.overall,
      verdict: r.verdict,
      detected_paper_type: r.detected_paper_type,
      priority: r.priority,
      result_json: r,
    }));

    const { error } = await supabase.from('evaluations').insert(rows);
    if (error) {
      console.error('Failed to save batch evaluations:', error);
    }
  }, [user, getToken]);

  const deleteEvaluation = useCallback(async (id: string) => {
    if (!user) return;
    const token = await getToken({ template: 'Supabase' });
    const supabase = makeSupabaseClient(token ?? null);

    const { error } = await supabase
      .from('evaluations')
      .delete()
      .eq('id', id)
      .eq('user_id', user.id);

    if (error) {
      console.error('Failed to delete evaluation:', error);
    } else {
      setHistory(prev => prev.filter(h => h.id !== id));
    }
  }, [user, getToken]);

  const totalTimeSavedMinutes = history.reduce((total, entry) => {
    const result = entry.result_json;
    if (!result?.stats?.word_count) return total;

    const fullReadMinutes = Math.round(result.stats.word_count / 220);
    let actualMinutes: number;
    switch (result.what_to_read) {
      case 'full': actualMinutes = fullReadMinutes; break;
      case 'methodology_only': actualMinutes = Math.round(fullReadMinutes * 0.25); break;
      case 'abstract_only': actualMinutes = 3; break;
      case 'skip': actualMinutes = 0; break;
      default: actualMinutes = fullReadMinutes;
    }
    return total + Math.max(0, fullReadMinutes - actualMinutes);
  }, 0);

  return {
    history,
    loading,
    totalCount: history.length,
    totalTimeSavedMinutes,
    saveEvaluation,
    saveBatch,
    deleteEvaluation,
    refetch: () => setRefreshKey(k => k + 1),
  };
}